// Talentspeicher in Discover. Reine Funktionen über dem Ergebnis von
// useTalentStorage (features/pricing/hooks.js), ohne React und ohne Imports,
// damit Jest sie direkt testen kann.
//
// storage ist { pending, isLocked(id), isPendingId(id), lockedIds,
// isOwnerLocked(owner), isPendingOwner(owner) }. Gezählt und gesperrt wird je
// Person (owner). isLocked ist nur in enforced für die Spaces der Personen ab
// Rang N+1 true, dann für alle ihre Spaces, auch die, die selbst nicht zählen,
// und bei pending für jede ID. isPendingId gilt zusätzlich für eine ID, die im
// Talents-Cache fehlt, bis er danach einmal geladen hat (etwa ein neues
// Talent per Deep Link). isOwnerLocked und isPendingOwner gelten genauso je
// owner, so ist auch ein Space gesperrt, der gar nicht in den Daten steht
// (Mitarbeitende), wenn seine Person gesperrt ist. Fehlen die owner-Funktionen,
// wird nur je ID geprüft. In legacy und preview ist nichts gesperrt, dann
// geben alle Funktionen die Eingabe unverändert zurück und Discover verhält
// sich wie bisher.

export const DISCOVER_TALENT_LOCK = Object.freeze({
  NONE: 'none',
  PENDING: 'pending',
  LOCKED: 'locked',
  UNLOCKED: 'unlocked'
})

const isLockedIn = (storage, id) =>
  typeof storage?.isLocked === 'function' && storage.isLocked(id) === true

const isPendingIn = (storage, id) =>
  !!storage?.pending ||
  (typeof storage?.isPendingId === 'function' && storage.isPendingId(id))

const hasLocks = (storage) =>
  !!storage?.pending || (storage?.lockedIds?.size ?? 0) > 0

// Endgültig gesperrt, ohne die IDs, die nur nachladen
const isDecidedLockedIn = (storage, id) => !!storage?.lockedIds?.has?.(id)

const isSetOwner = (owner) =>
  owner !== null && owner !== undefined && owner !== ''

// Gesperrt oder offen je owner, ohne owner-Funktionen nie
const isOwnerLockedIn = (storage, owner) =>
  isSetOwner(owner) &&
  typeof storage?.isOwnerLocked === 'function' &&
  storage.isOwnerLocked(owner) === true

const isOwnerPendingIn = (storage, owner) =>
  isSetOwner(owner) &&
  (!!storage?.pending ||
    (typeof storage?.isPendingOwner === 'function' &&
      storage.isPendingOwner(owner) === true))

/**
 * Zustand eines ausgewählten Talents. pending heißt, es ist noch nicht
 * gezählt oder fehlt noch im nachladenden Cache, dann laufen keine
 * Ergebnis-Queries und es gibt Skeletons. Mit owner zählt zusätzlich die
 * Sperre der Person, Ergebnisse laden am owner.
 *
 * @param {string|null|undefined} talentId Space-ID aus ?selection=
 * @param {object} storage Ergebnis von useTalentStorage
 * @param {string} [owner] owner des Space, sobald bekannt
 * @returns {'none'|'pending'|'locked'|'unlocked'}
 */
export const discoverTalentLock = (talentId, storage, owner) => {
  if (talentId === null || talentId === undefined || talentId === '') {
    return DISCOVER_TALENT_LOCK.NONE
  }
  if (isPendingIn(storage, talentId) || isOwnerPendingIn(storage, owner)) {
    return DISCOVER_TALENT_LOCK.PENDING
  }

  return isLockedIn(storage, talentId) || isOwnerLockedIn(storage, owner)
    ? DISCOVER_TALENT_LOCK.LOCKED
    : DISCOVER_TALENT_LOCK.UNLOCKED
}

/** Echte Ergebnisse (Matching, Profile, PDF) nur außerhalb von Sperre und pending */
export const canLoadTalentResults = (lock) =>
  lock !== DISCOVER_TALENT_LOCK.PENDING && lock !== DISCOVER_TALENT_LOCK.LOCKED

/**
 * Teilt eine Auswahl von Space-IDs in freie und gesperrte. Bei pending, auch
 * für nur eine ID der Auswahl, ist nichts frei, damit keine Teamwerte
 * gesperrter Talente geladen werden.
 *
 * @param {string[]} ids
 * @param {object} storage
 * @returns {{ pending: boolean, unlockedIds: string[], lockedIds: string[] }}
 */
export const splitTalentSelection = (ids, storage) => {
  const list = Array.isArray(ids) ? ids : []

  if (storage?.pending || list.some((id) => isPendingIn(storage, id))) {
    return { pending: true, unlockedIds: [], lockedIds: [] }
  }

  const unlockedIds = []
  const lockedIds = []
  list.forEach((id) => {
    if (isLockedIn(storage, id)) lockedIds.push(id)
    else unlockedIds.push(id)
  })

  return { pending: false, unlockedIds, lockedIds }
}

// Ein Eintrag mit id und optional owner
const isTalentLockedIn = (storage, talent) =>
  isLockedIn(storage, talent.id) || isOwnerLockedIn(storage, talent.owner)

const isTalentPendingIn = (storage, talent) =>
  isPendingIn(storage, talent.id) || isOwnerPendingIn(storage, talent.owner)

/**
 * Markiert Einträge einer Talentauswahl (mit id, optional owner) als
 * gesperrt. Ohne Sperren kommt dieselbe Referenz zurück, sonst eine neue
 * Liste mit isLocked an den gesperrten Einträgen. Gesperrt ist ein Eintrag
 * auch, wenn seine Person gesperrt ist, etwa ein Space, der selbst nicht
 * zählt. Einträge, die noch im nachladenden Cache fehlen, gelten bis dahin
 * ebenfalls als gesperrt.
 */
export const markLockedTalents = (talents, storage) => {
  if (!Array.isArray(talents)) return talents

  let changed = false
  const marked = talents.map((talent) => {
    if (!talent || !isTalentLockedIn(storage, talent)) return talent
    changed = true
    return { ...talent, isLocked: true }
  })

  return changed ? marked : talents
}

/**
 * Ob für einen Eintrag der Liste der Talentspeicher noch nicht entschieden
 * ist (pending, die ID oder der owner fehlt im nachladenden Cache). Aufrufer
 * zeigen dann einen Ladezustand statt eines Schlosses.
 */
export const hasPendingTalents = (talents, storage) =>
  !!storage?.pending ||
  (Array.isArray(talents) &&
    talents.some((talent) => !!talent && isTalentPendingIn(storage, talent)))

const spaceIdsByOwner = (spaces) => {
  const byOwner = new Map()
  ;(Array.isArray(spaces) ? spaces : []).forEach((space) => {
    if (!space?.owner || space.id === null || space.id === undefined) return
    const ids = byOwner.get(space.owner) || []
    ids.push(space.id)
    byOwner.set(space.owner, ids)
  })
  return byOwner
}

/**
 * Ob eine Person (owner) gesperrt ist. Mit isOwnerLocked entscheidet der
 * Talentspeicher direkt. Sonst über ihre Spaces in der Liste, alle Spaces
 * einer Person teilen die Sperre. Ein endgültig gesperrter Space sperrt also
 * die Person, auch wenn sie daneben einen Space hat, der nicht zählt
 * (Beispieldaten). Sonst ist sie frei, sobald ein Space entschieden und frei
 * ist. Fehlen alle ihre Spaces noch im nachladenden Cache, gilt sie bis dahin
 * als gesperrt.
 *
 * @param {string} owner
 * @param {string[]} ids Space-IDs der Person in der Liste
 * @param {object} storage
 */
const isPersonLockedIn = (owner, ids, storage) => {
  if (isOwnerLockedIn(storage, owner)) return true
  if (ids.length === 0) return false
  if (ids.some((id) => isDecidedLockedIn(storage, id))) return true
  return !ids.some((id) => !isLockedIn(storage, id))
}

/**
 * Space-ID, zu der ein Vorschlag (per owner) führt. Ohne Sperren ist das wie
 * bisher der erste Space mit diesem owner, ebenso für eine gesperrte Person.
 * Gesperrt wird je Person, ihre Spaces sind also alle frei oder alle
 * gesperrt. Für eine freie Person wird ein Space bevorzugt, der schon
 * entschieden ist, statt eines, der noch nachlädt.
 */
export const pickSpaceIdForOwner = (spaces, owner, storage) => {
  const ids = spaceIdsByOwner(spaces).get(owner) || []
  if (ids.length === 0) return undefined
  if (isPersonLockedIn(owner, ids, storage)) return ids[0]

  return ids.find((id) => !isLockedIn(storage, id)) ?? ids[0]
}

const defaultOwnerOf = (suggestion) => suggestion?.talentId

/**
 * Filtert Vorschläge (matchBatchIfYouCan, je owner), deren Talent gesperrt
 * ist. Gezählt und gesperrt wird je Person, gesperrt ist ein owner also, wenn
 * seine Person hinter dem Limit liegt (isPersonLockedIn). Ein owner, den der
 * Talents-Cache noch nicht kennt, kann eine neue Person hinter dem Limit
 * sein. Er fehlt, bis der Cache danach einmal geladen hat (isPendingOwner),
 * auch ohne Sperren. Danach zählt ein unbekannter owner nicht zum
 * Talentspeicher und bleibt. Bei pending gibt es keine Vorschläge. Gibt es
 * Sperren, aber weder owner-Prüfung noch Talentliste zum Zuordnen, ebenfalls
 * nicht, sonst stünden Scores gesperrter Talente im Assistenten. Bleibt alles,
 * kommt dieselbe Referenz zurück.
 *
 * @param {object[]} suggestions
 * @param {{ spaces?: object[], storage: object,
 *   ownerOf?: (suggestion) => string }} options
 */
export const filterLockedSuggestions = (
  suggestions,
  { spaces, storage, ownerOf = defaultOwnerOf } = {}
) => {
  if (!Array.isArray(suggestions)) return suggestions
  if (storage?.pending) return []

  const checksOwners = typeof storage?.isOwnerLocked === 'function'
  if (!checksOwners && !Array.isArray(spaces) && hasLocks(storage)) return []

  const byOwner = spaceIdsByOwner(spaces)
  const kept = suggestions.filter((suggestion) => {
    const owner = ownerOf(suggestion)
    if (isOwnerPendingIn(storage, owner)) return false
    return !isPersonLockedIn(owner, byOwner.get(owner) || [], storage)
  })

  return kept.length === suggestions.length ? suggestions : kept
}
